/**
 * 온라인 상태 전환 시 자동 동기화 훅
 */

import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useMutation, useApolloClient } from "@apollo/client";
import { createLogger } from "@ict/shared";
import { useNetworkStatus } from "./useNetworkStatus";
import { useToast } from "@/contexts/ToastContext";
import { useSyncQueueContext } from "@/contexts/SyncQueueContext";
import {
  executeSyncQueue,
  syncPhotos,
  syncMachineTemplates,
  type SyncExecutionResult,
} from "@/lib/db/sync";
import { fetchMachineDocument as fetchMachineDocumentUtil } from "@/lib/sync/fetchMachineDocument";
import { updateQueueVersionForDocument } from "@/lib/db/changes";
import {
  UPDATE_MACHINE_DOCUMENT_ITEMS_MUTATION,
  UPDATE_MACHINE_INFO_MUTATION,
} from "@/lib/graphql/inspection";
import {
  CREATE_EQUIPMENT_MUTATION,
  DELETE_EQUIPMENT_MUTATION,
  executeEquipmentDeleteMutation,
  MACHINE_DOCUMENT_VERSION_QUERY,
  UPDATE_MACHINE_DOCUMENT_PICTURES_MUTATION,
  type UpdateMachineDocumentPicturesData,
  type UpdateMachineDocumentPicturesVariables,
} from "@/lib/graphql/equipment";
import {
  ADD_MEASURE_MUTATION,
  UPDATE_MEASURE_MUTATION,
  DELETE_MEASURE_MUTATION,
} from "@/lib/graphql/measures";
import {
  createPhotoEtcUploadedHandler,
  createMeasurePhotoUploadedHandler,
} from "@/lib/sync/photoCallbacks";

const logger = createLogger({ module: "AutoSync" });

interface UseAutoSyncOptions {
  /** 동기화 성공 시 콜백 */
  onSyncComplete?: (result: SyncExecutionResult) => void;
  /** 동기화 실패 시 콜백 */
  onSyncError?: (error: Error) => void;
  /** 자동 동기화 비활성화 */
  disabled?: boolean;
}

interface UseAutoSyncReturn {
  /** 동기화 진행 중 여부 */
  isSyncing: boolean;
  /** 마지막 동기화 결과 */
  lastSyncResult: SyncExecutionResult | null;
  /** 대기 중인 변경사항 수 */
  pendingCount: number;
  /** 수동 동기화 트리거 */
  triggerSync: () => Promise<void>;
}

export function useAutoSync(options: UseAutoSyncOptions = {}): UseAutoSyncReturn {
  const { onSyncComplete, onSyncError, disabled = false } = options;
  const { isOffline, isLoading } = useNetworkStatus();
  const { showToast } = useToast();
  const apolloClient = useApolloClient();
  const { pendingCount, refreshPendingCount: refreshContextPendingCount } = useSyncQueueContext();

  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<SyncExecutionResult | null>(null);

  // 이전 오프라인 상태를 추적
  const wasOfflineRef = useRef<boolean | null>(null);

  // mutation 함수들
  const [updateMachineDocumentItems] = useMutation(UPDATE_MACHINE_DOCUMENT_ITEMS_MUTATION);
  const [updateMachineInfo] = useMutation(UPDATE_MACHINE_INFO_MUTATION);
  const [createEquipment] = useMutation(CREATE_EQUIPMENT_MUTATION);
  const [deleteEquipment] = useMutation(DELETE_EQUIPMENT_MUTATION);
  const [updatePictures] = useMutation<
    UpdateMachineDocumentPicturesData,
    UpdateMachineDocumentPicturesVariables
  >(UPDATE_MACHINE_DOCUMENT_PICTURES_MUTATION);
  const [addMeasureMutation] = useMutation(ADD_MEASURE_MUTATION);
  const [updateMeasureMutation] = useMutation(UPDATE_MEASURE_MUTATION);
  const [deleteMeasureMutation] = useMutation(DELETE_MEASURE_MUTATION);

  // 사진 동기화 콜백 (공유 유틸리티 사용)
  const handlePhotoEtcUploaded = useMemo(
    () => createPhotoEtcUploadedHandler(apolloClient, updatePictures),
    [apolloClient, updatePictures]
  );

  const handleMeasurePhotoUploaded = useMemo(
    () => createMeasurePhotoUploadedHandler(apolloClient, updateMeasureMutation),
    [apolloClient, updateMeasureMutation]
  );

  // 문서 버전 조회 (VERSION_CONFLICT 해결용)
  const fetchDocumentVersion = useCallback(
    async (projectId: string, docKey: string, machineId: string): Promise<number | null> => {
      try {
        const { data } = await apolloClient.query({
          query: MACHINE_DOCUMENT_VERSION_QUERY,
          variables: { projectId, docKey, machineId },
          fetchPolicy: "network-only",
        });
        return data?.machineDocument?.version ?? null;
      } catch (error) {
        logger.error("버전 조회 실패:", error);
        return null;
      }
    },
    [apolloClient]
  );

  // 설비 문서 조회 (itemId 매핑용, Feature 326)
  const fetchMachineDocument = useCallback(
    (projectId: string, docKey: string, machineId: string) =>
      fetchMachineDocumentUtil(apolloClient, projectId, docKey, machineId),
    [apolloClient]
  );

  // 동기화 실행
  const executeSync = useCallback(async () => {
    if (isSyncing || disabled) {
      return;
    }

    const count = await refreshContextPendingCount();
    if (count === 0) {
      return;
    }

    setIsSyncing(true);
    showToast(`${count}건 동기화 중...`, "info");

    try {
      // VERSION_CONFLICT 재시도 헬퍼
      const retryWithUpdatedVersion = async (
        opts: { variables: Record<string, unknown> },
        mutationFn: typeof updateMachineDocumentItems | typeof updateMachineInfo,
        responseKey: "updateMachineDocumentItems" | "updateMachineInfo"
      ) => {
        const input = opts.variables.input as Record<string, unknown>;
        const result = await mutationFn(opts);

        if (result.errors) {
          logger.error("GraphQL 에러:", result.errors);
          throw new Error(result.errors[0]?.message || "GraphQL error");
        }

        const response = result.data?.[responseKey];

        // VERSION_CONFLICT 발생 시 서버 버전 조회 후 재시도
        if (response && !response.success && response.errorCode === "VERSION_CONFLICT") {
          const serverVersion = await fetchDocumentVersion(
            input.projectId as string,
            input.docKey as string,
            input.machineId as string
          );

          if (serverVersion !== null) {
            // 큐 항목 버전 업데이트
            await updateQueueVersionForDocument(
              input.projectId as string,
              input.docKey as string,
              input.machineId as string,
              serverVersion
            );

            // 새 버전으로 재시도
            const retryOpts = {
              variables: {
                input: {
                  ...input,
                  expectedVersion: serverVersion,
                  clientMutationId: `sync-retry-${Date.now()}`,
                },
              },
            };

            const retryResult = await mutationFn(retryOpts);

            if (retryResult.errors) {
              logger.error("재시도 GraphQL 에러:", retryResult.errors);
              throw new Error(retryResult.errors[0]?.message || "GraphQL error");
            }

            const retryResponse = retryResult.data?.[responseKey];

            if (retryResponse && !retryResponse.success) {
              logger.error("재시도 실패:", retryResponse.errorCode, retryResponse.error);
              throw new Error(retryResponse.error || retryResponse.errorCode || "Retry failed");
            }

            // 재시도 성공 시 버전 업데이트
            if (retryResponse?.newVersion) {
              await updateQueueVersionForDocument(
                input.projectId as string,
                input.docKey as string,
                input.machineId as string,
                retryResponse.newVersion
              );
            }
            return retryResult;
          } else {
            logger.error("서버 버전 조회 실패 - 재시도 불가");
            throw new Error("VERSION_CONFLICT: 서버 버전 조회 실패");
          }
        }

        // 다른 비즈니스 에러
        if (response && !response.success) {
          logger.error("비즈니스 에러:", response.errorCode, response.error);
          throw new Error(response.error || response.errorCode || "Mutation failed");
        }

        // 성공 시 버전 업데이트
        if (response?.newVersion && input.projectId && input.docKey && input.machineId) {
          await updateQueueVersionForDocument(
            input.projectId as string,
            input.docKey as string,
            input.machineId as string,
            response.newVersion
          );
        }

        return result;
      };

      // 타입별 mutation 라우팅 함수 (newVersion 반환으로 버전 전파 지원)
      const mutate = async (opts: {
        variables: Record<string, unknown>;
      }): Promise<{ newVersion?: number }> => {
        // variables에서 타입을 추론
        const input = opts.variables.input as Record<string, unknown> | undefined;

        // equipment 타입: equipmentOperation 필드로 라우팅 (#457, 공유 유틸리티 사용)
        const equipmentOperation = input?.equipmentOperation as string | undefined;
        if (equipmentOperation === "delete") {
          return executeEquipmentDeleteMutation(input!, deleteEquipment);
        }

        // machineKey가 있으면 equipment create 타입 (버전 관리 없음)
        if (input && "machineKey" in input) {
          const result = await createEquipment(opts);
          if (result.errors) {
            logger.error("GraphQL 에러:", result.errors);
            throw new Error(result.errors[0]?.message || "GraphQL error");
          }
          // Feature 326: equipment create 결과 반환 (ID 매핑용)
          return result as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        }

        // measure 타입: measureOperation 필드로 명시적 라우팅
        const measureOperation = input?.measureOperation as string | undefined;
        if (measureOperation) {
          const { measureOperation: _op, ...serverInput } = input!;

          let mutationFn = updateMeasureMutation;
          let responseKey = "updateMeasure";

          if (measureOperation === "create") {
            mutationFn = addMeasureMutation;
            responseKey = "addMeasure";
          } else if (measureOperation === "delete") {
            mutationFn = deleteMeasureMutation;
            responseKey = "deleteMeasure";
          }

          const serverOpts = { variables: { input: serverInput } };
          const mutationResult = await mutationFn(serverOpts);
          if (mutationResult.errors) {
            throw new Error(mutationResult.errors[0]?.message || "GraphQL error");
          }
          const response = (mutationResult.data as Record<string, unknown>)?.[responseKey] as
            | { success?: boolean; error?: string; errorCode?: string; newVersion?: number }
            | undefined;

          // VERSION_CONFLICT 재시도
          if (response && !response.success && response.errorCode === "VERSION_CONFLICT") {
            const serverVersion = await fetchDocumentVersion(
              serverInput.projectId as string,
              serverInput.docKey as string,
              serverInput.machineId as string
            );
            if (serverVersion !== null) {
              const retryOpts = {
                variables: {
                  input: {
                    ...serverInput,
                    expectedVersion: serverVersion,
                    clientMutationId: `sync-retry-${Date.now()}`,
                  },
                },
              };
              const retryResult = await mutationFn(retryOpts);
              if (retryResult.errors) {
                throw new Error(retryResult.errors[0]?.message || "GraphQL error");
              }
              const retryResponse = (retryResult.data as Record<string, unknown>)?.[
                responseKey
              ] as typeof response;
              if (retryResponse && !retryResponse.success) {
                throw new Error(retryResponse.error || retryResponse.errorCode || "Retry failed");
              }
              return { newVersion: retryResponse?.newVersion };
            }
            throw new Error("VERSION_CONFLICT: 서버 버전 조회 실패");
          }

          if (response && !response.success) {
            throw new Error(response.error || response.errorCode || "Mutation failed");
          }
          return { newVersion: response?.newVersion };
        }

        // machine 타입 감지: patches 배열의 첫 번째 항목에 itemId가 없으면 machine 타입
        // - machine 타입 patches: { field, value } (MachineInfoPatchInput)
        // - checklist/data 타입 patches: { itemId, itemType, field, value } (ItemPatchInput)
        if (input && "patches" in input && "machineId" in input) {
          const patches = input.patches as Array<Record<string, unknown>> | undefined;
          if (patches && patches.length > 0 && !("itemId" in patches[0])) {
            const result = await retryWithUpdatedVersion(
              opts,
              updateMachineInfo,
              "updateMachineInfo"
            );
            return { newVersion: result.data?.updateMachineInfo?.newVersion };
          }
        }

        // 기본: checklist/data 타입
        const result = await retryWithUpdatedVersion(
          opts,
          updateMachineDocumentItems,
          "updateMachineDocumentItems"
        );
        return { newVersion: result.data?.updateMachineDocumentItems?.newVersion };
      };

      // GraphQL mutation 동기화를 먼저 실행 (equipment create, measure create 등)
      // 사진 동기화 시 서버에 measure/equipment가 존재해야 URL 업데이트 가능
      // executeSyncQueue는 photo 타입을 자동 건너뜀 (item.type !== "photo")
      const result = await executeSyncQueue(
        mutate,
        fetchMachineDocument, // Feature 326: itemId 매핑을 위한 서버 문서 조회
        undefined, // fetchServerData - 충돌 감지 없이 진행
        true, // autoResolveConflicts
        undefined, // defaultResolution
        {
          // 개별 항목 진행 시에는 토스트 생략 (너무 빈번함)
          // 전체 완료 시에만 결과 요약 표시
        }
      );

      // 사진 동기화 (REST API 업로드 + measure/pictures URL 업데이트)
      // executeSyncQueue 이후 실행: 서버에 measure가 존재하는 상태에서 photo URL 업데이트
      const photoResult = await syncPhotos(handlePhotoEtcUploaded, handleMeasurePhotoUploaded);

      // 결과 합산
      const combinedResult: SyncExecutionResult = {
        success: result.success && photoResult.success,
        syncedCount: result.syncedCount + photoResult.syncedCount,
        failedCount: result.failedCount + photoResult.failedCount,
        conflictCount: result.conflictCount + photoResult.conflictCount,
        results: [...result.results, ...photoResult.results],
        conflicts: [...result.conflicts, ...photoResult.conflicts],
      };

      setLastSyncResult(combinedResult);

      if (combinedResult.conflictCount > 0) {
        // 충돌 발생 시 경고 토스트
        showToast("충돌 발생: SyncPage에서 확인하세요", "warning");
      } else if (combinedResult.success && combinedResult.syncedCount > 0) {
        // 전체 성공
        showToast(`${combinedResult.syncedCount}개 항목 동기화 완료`, "success");
        onSyncComplete?.(combinedResult);
      } else if (combinedResult.failedCount > 0 && combinedResult.syncedCount > 0) {
        // 부분 실패
        showToast(
          `${combinedResult.syncedCount}개 완료, ${combinedResult.failedCount}개 실패`,
          "warning"
        );
      } else if (combinedResult.failedCount > 0) {
        // 전체 실패
        showToast(`동기화 실패: ${combinedResult.failedCount}개 항목`, "error");
      }

      // 대기 카운트 업데이트 (Context를 통해 모든 소비자에게 전파)
      await refreshContextPendingCount();
    } catch (error) {
      logger.error("동기화 오류:", error);
      const err = error instanceof Error ? error : new Error("동기화 실패");
      onSyncError?.(err);
      showToast("동기화 실패: 네트워크 오류", "error");
    } finally {
      setIsSyncing(false);
    }
  }, [
    isSyncing,
    disabled,
    updateMachineDocumentItems,
    updateMachineInfo,
    createEquipment,
    deleteEquipment,
    addMeasureMutation,
    updateMeasureMutation,
    deleteMeasureMutation,
    fetchDocumentVersion,
    fetchMachineDocument,
    handlePhotoEtcUploaded,
    handleMeasurePhotoUploaded,
    refreshContextPendingCount,
    showToast,
    onSyncComplete,
    onSyncError,
  ]);

  // 온라인 상태 전환 감지 및 자동 동기화
  useEffect(() => {
    // 초기 로딩 중에는 무시
    if (isLoading) {
      return;
    }

    // 이전 상태가 오프라인이었고, 현재 온라인으로 전환되었을 때
    if (wasOfflineRef.current === true && isOffline === false) {
      // 온라인으로 전환됨 - 동기화 시작
      executeSync();
    }

    // 현재 상태 저장
    wasOfflineRef.current = isOffline;
  }, [isOffline, isLoading, executeSync]);

  // 초기 대기 카운트 로드 (Context의 refreshPendingCount 활용)
  useEffect(() => {
    refreshContextPendingCount();
  }, [refreshContextPendingCount]);

  // Feature 288: 설비 템플릿 캐싱 (온라인 상태일 때)
  useEffect(() => {
    // 로딩 중이거나 오프라인이면 무시
    if (isLoading || isOffline) {
      return;
    }

    // 온라인 상태일 때 템플릿 동기화 (백그라운드)
    const syncTemplates = async () => {
      try {
        const result = await syncMachineTemplates();
        if (result.success && !result.skipped) {
          logger.debug(`설비 템플릿 ${result.syncedCount}개 캐싱 완료`);
        }
      } catch (error) {
        // 템플릿 동기화 실패는 치명적이지 않음 - 조용히 로깅
        logger.warn("설비 템플릿 캐싱 실패:", error);
      }
    };

    syncTemplates();
  }, [isLoading, isOffline]);

  return {
    isSyncing,
    lastSyncResult,
    pendingCount,
    triggerSync: executeSync,
  };
}
